import React, { useState, useEffect, useCallback, useRef } from "react";
import { 
  Mail, Send, Inbox, FileText, Paperclip, Trash2, Star, AlertCircle,
  RefreshCw, Search, Plus, ChevronDown, Sparkles, X, Check, Clock,
  ArrowLeft, Reply, Forward, Tag, User, Link2, Unlink, Bell, Archive,
  Package, MessageCircle, AlertTriangle, Filter, ChevronRight, ExternalLink,
  Folder, FolderOpen, ListX, MailX, Loader2
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Catégories d'emails
const EMAIL_CATEGORIES = {
  urgent: { label: "Urgents", icon: AlertTriangle, color: "text-red-400", bg: "bg-red-500/20" },
  important: { label: "Important", icon: Star, color: "text-amber-400", bg: "bg-amber-500/20" },
  fournisseur: { label: "Fournisseurs", icon: Package, color: "text-blue-400", bg: "bg-blue-500/20" },
  echanges: { label: "Échanges", icon: MessageCircle, color: "text-emerald-400", bg: "bg-emerald-500/20" },
  rattache: { label: "Rattaché client", icon: Link2, color: "text-purple-400", bg: "bg-purple-500/20" },
  autre: { label: "Autres", icon: Mail, color: "text-slate-400", bg: "bg-slate-500/20" }
};

// Icônes pour les dossiers
const FOLDER_ICONS = {
  inbox: Inbox,
  send: Send,
  "file-text": FileText,
  trash: Trash2,
  "alert-circle": AlertCircle,
  archive: Archive,
  folder: Folder,
  upload: Send
};

// Badge de catégorie
const CategoryBadge = ({ category }) => {
  const cat = EMAIL_CATEGORIES[category] || EMAIL_CATEGORIES.autre;
  const Icon = cat.icon;
  return (
    <span className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 ${cat.bg} ${cat.color}`}>
      <Icon className="w-3 h-3" />
      {cat.label}
    </span>
  );
};

// Badge client rattaché
const ClientBadge = ({ client, onDetach }) => (
  <div className="flex items-center gap-2 px-2 py-1 bg-purple-500/20 rounded-lg">
    <User className="w-3 h-3 text-purple-400" />
    <span className="text-xs text-purple-300">
      {client.nom} {client.prenom} - Dossier #{client.numero_dossier}
    </span>
    {onDetach && (
      <button onClick={onDetach} className="p-0.5 hover:bg-purple-500/30 rounded" title="Détacher">
        <Unlink className="w-3 h-3 text-purple-400" />
      </button>
    )}
  </div>
);

export default function EmailManager({ authUser }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [emails, setEmails] = useState([]);
  const [emailTotal, setEmailTotal] = useState(0);
  const [emailHasMore, setEmailHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const emailsCountRef = useRef(0);
  const EMAIL_PAGE_SIZE = 30;
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("inbox");
  const [activeCategory, setActiveCategory] = useState("all");
  
  // Dossiers IMAP
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("INBOX");
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [syncingFolder, setSyncingFolder] = useState(null);
  
  // Listes de diffusion
  const [mailingLists, setMailingLists] = useState([]);
  const [loadingMailingLists, setLoadingMailingLists] = useState(false);
  const [unsubscribing, setUnsubscribing] = useState(null);
  
  // Email sélectionné pour lecture
  const [selectedEmail, setSelectedEmail] = useState(null);
  
  // Suggestion de réponse automatique
  const [suggestedReply, setSuggestedReply] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  
  // Composer email
  const [composing, setComposing] = useState(false);
  const [emailDraft, setEmailDraft] = useState({ to: "", subject: "", body: "", client_id: "" });
  const [sending, setSending] = useState(false);
  
  // Autocomplétion des emails
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  // Christelle IA
  const [showChristellePanel, setShowChristellePanel] = useState(false);
  const [christelleLoading, setChristelleLoading] = useState(false);
  const [christelleResponse, setChristelleResponse] = useState(null);
  const [christelleQuery, setChristelleQuery] = useState("");
  
  // Classification
  const [classifying, setClassifying] = useState(false);
  const [classificationStats, setClassificationStats] = useState({});
  
  // Recherche
  const [searchQuery, setSearchQuery] = useState("");
  
  // Clients pour rattachement
  const [clients, setClients] = useState([]);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState("");

  // Charger les comptes email
  const loadAccounts = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/api/email/accounts?user_email=${encodeURIComponent(authUser?.email || "")}`);
      if (resp.ok) {
        const data = await resp.json();
        setAccounts(data.accounts || []);
        if (data.accounts?.length > 0 && !selectedAccount) {
          setSelectedAccount(data.accounts[0]);
        }
      }
    } catch (e) {
      console.error("Erreur chargement comptes:", e);
    }
  }, [authUser?.email, selectedAccount]);

  // Charger les dossiers IMAP
  const loadFolders = useCallback(async () => {
    if (!selectedAccount?.email_address) return;
    setLoadingFolders(true);
    try {
      const resp = await authFetch(`${API}/api/imap/folders/${encodeURIComponent(selectedAccount.email_address)}`);
      if (resp.ok) {
        const data = await resp.json();
        setFolders(data.folders || []);
      }
    } catch (e) {
      console.error("Erreur chargement dossiers:", e);
      // Créer des dossiers par défaut si échec
      setFolders([
        { id: "INBOX", name: "INBOX", display_name: "Boîte de réception", icon: "inbox" },
        { id: "Sent", name: "Sent", display_name: "Éléments envoyés", icon: "send" }
      ]);
    }
    setLoadingFolders(false);
  }, [selectedAccount?.email_address]);

  // Charger les emails
  const loadEmails = useCallback(async (append = false) => {
    if (!selectedAccount) return;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const folderParam = selectedFolder ? `&folder=${encodeURIComponent(selectedFolder)}` : "";
      const skip = append ? emailsCountRef.current : 0;
      const resp = await authFetch(
        `${API}/api/email/inbox?account_id=${selectedAccount.id}&user_email=${encodeURIComponent(authUser?.email || "")}${folderParam}&skip=${skip}&limit=${EMAIL_PAGE_SIZE}`
      );
      if (resp.ok) {
        const data = await resp.json();
        const newEmails = data.emails || [];
        
        if (append) {
          setEmails(prev => {
            const updated = [...prev, ...newEmails];
            emailsCountRef.current = updated.length;
            return updated;
          });
        } else {
          setEmails(newEmails);
          emailsCountRef.current = newEmails.length;
        }
        setEmailTotal(data.total || 0);
        setEmailHasMore(data.has_more || false);
        
        // Calculer les stats par catégorie
        if (!append) {
          const stats = {};
          newEmails.forEach(email => {
            const cat = email.category || "autre";
            stats[cat] = (stats[cat] || 0) + 1;
          });
          setClassificationStats(stats);
        } else {
          setClassificationStats(prev => {
            const stats = { ...prev };
            newEmails.forEach(email => {
              const cat = email.category || "autre";
              stats[cat] = (stats[cat] || 0) + 1;
            });
            return stats;
          });
        }
      }
    } catch (e) {
      console.error("Erreur chargement emails:", e);
    }
    setLoading(false);
    setLoadingMore(false);
  }, [selectedAccount, authUser?.email, selectedFolder]);

  // Charger les listes de diffusion
  const loadMailingLists = useCallback(async () => {
    if (!selectedAccount) return;
    setLoadingMailingLists(true);
    try {
      const resp = await authFetch(
        `${API}/api/email/mailing-lists?account_id=${selectedAccount.id}&user_email=${encodeURIComponent(authUser?.email || "")}`
      );
      if (resp.ok) {
        const data = await resp.json();
        setMailingLists(data.mailing_lists || []);
      }
    } catch (e) {
      console.error("Erreur chargement listes de diffusion:", e);
    }
    setLoadingMailingLists(false);
  }, [selectedAccount, authUser?.email]);

  // Synchroniser un dossier
  const syncFolder = async (folderName) => {
    if (!selectedAccount?.email_address) return;
    setSyncingFolder(folderName);
    try {
      const resp = await authFetch(
        `${API}/api/imap/fetch-folder/${encodeURIComponent(selectedAccount.email_address)}?folder=${encodeURIComponent(folderName)}&since_days=30&limit=100`,
        { method: "POST" }
      );
      if (resp.ok) {
        const data = await resp.json();
        console.log(`Sync ${folderName}: ${data.new_emails} nouveaux emails`);
        // Recharger les emails si c'est le dossier actif
        if (folderName === selectedFolder) {
          loadEmails();
        }
        // Recharger les listes de diffusion si des nouveaux emails
        if (data.mailing_lists_found?.length > 0) {
          loadMailingLists();
        }
      }
    } catch (e) {
      console.error("Erreur sync dossier:", e);
    }
    setSyncingFolder(null);
  };

  // Sélectionner un dossier et le synchroniser si vide
  const selectFolder = async (folderId) => {
    setSelectedFolder(folderId);
    setActiveView("inbox");
    setActiveCategory("all");
    
    // Charger les emails du dossier (reset pagination)
    if (!selectedAccount) return;
    setLoading(true);
    setEmails([]);
    try {
      const folderParam = `&folder=${encodeURIComponent(folderId)}`;
      const resp = await authFetch(
        `${API}/api/email/inbox?account_id=${selectedAccount.id}&user_email=${encodeURIComponent(authUser?.email || "")}${folderParam}&skip=0&limit=${EMAIL_PAGE_SIZE}`
      );
      if (resp.ok) {
        const data = await resp.json();
        const emailList = data.emails || [];
        setEmails(emailList);
        setEmailTotal(data.total || 0);
        setEmailHasMore(data.has_more || false);
        
        // Si le dossier est vide, lancer une synchronisation automatique
        if (emailList.length === 0 && selectedAccount?.email_address && folderId !== "INBOX") {
          console.log(`Dossier ${folderId} vide, synchronisation automatique...`);
          setSyncingFolder(folderId);
          try {
            const syncResp = await authFetch(
              `${API}/api/imap/fetch-folder/${encodeURIComponent(selectedAccount.email_address)}?folder=${encodeURIComponent(folderId)}&since_days=60&limit=50`,
              { method: "POST" }
            );
            if (syncResp.ok) {
              const syncData = await syncResp.json();
              console.log(`Sync ${folderId}: ${syncData.new_emails} nouveaux emails`);
              const reloadResp = await authFetch(
                `${API}/api/email/inbox?account_id=${selectedAccount.id}&user_email=${encodeURIComponent(authUser?.email || "")}${folderParam}&skip=0&limit=${EMAIL_PAGE_SIZE}`
              );
              if (reloadResp.ok) {
                const reloadData = await reloadResp.json();
                setEmails(reloadData.emails || []);
                setEmailTotal(reloadData.total || 0);
                setEmailHasMore(reloadData.has_more || false);
              }
            }
          } catch (syncErr) {
            console.error("Erreur sync auto:", syncErr);
          }
          setSyncingFolder(null);
        }
        
        // Calculer les stats par catégorie
        const stats = {};
        emailList.forEach(email => {
          const cat = email.category || "autre";
          stats[cat] = (stats[cat] || 0) + 1;
        });
        setClassificationStats(stats);
      }
    } catch (e) {
      console.error("Erreur chargement emails dossier:", e);
    }
    setLoading(false);
  };

  // Recherche intelligente IA
  const [aiSearchQuery, setAiSearchQuery] = useState("");
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [aiSearching, setAiSearching] = useState(false);

  const handleAiSearch = async () => {
    if (!aiSearchQuery.trim() || !selectedAccount) return;
    setAiSearching(true);
    setAiSearchResults(null);
    try {
      const resp = await authFetch(`${API}/api/email/ai-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: aiSearchQuery,
          account_email: selectedAccount.email_address,
          user_email: authUser?.email
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setAiSearchResults(data);
        setActiveView("ai-search");
      } else {
        alert("Erreur lors de la recherche IA");
      }
    } catch (e) {
      console.error("Erreur recherche IA:", e);
      alert("Erreur lors de la recherche IA");
    }
    setAiSearching(false);
  };

  // Désabonnement d'une liste
  const handleUnsubscribe = async (list) => {
    if (!selectedAccount?.email_address) return;
    setUnsubscribing(list.id);
    try {
      const resp = await authFetch(`${API}/api/email/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          list_email: list.email,
          unsubscribe_link: list.unsubscribe_link,
          unsubscribe_email: list.unsubscribe_email,
          user_email: authUser?.email,
          account_email: selectedAccount.email_address
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          alert(`✅ ${data.message}`);
          // Retirer de la liste
          setMailingLists(prev => prev.filter(l => l.id !== list.id));
        } else {
          alert(`❌ Échec du désabonnement: ${data.message}`);
        }
      }
    } catch (e) {
      console.error("Erreur désabonnement:", e);
      alert("Erreur lors du désabonnement");
    }
    setUnsubscribing(null);
  };

  // Charger les brouillons
  const loadDrafts = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/api/email/drafts?user_email=${encodeURIComponent(authUser?.email || "")}`);
      if (resp.ok) {
        const data = await resp.json();
        setDrafts(data.drafts || []);
      }
    } catch (e) {
      console.error("Erreur chargement brouillons:", e);
    }
  }, [authUser?.email]);

  useEffect(() => {
    loadAccounts();
    loadDrafts();
  }, [loadAccounts, loadDrafts]);

  useEffect(() => {
    if (selectedAccount) {
      loadEmails();
      loadFolders();
      loadMailingLists();
    }
  }, [selectedAccount, loadEmails, loadFolders, loadMailingLists]);

  useEffect(() => {
    if (selectedAccount && selectedFolder) {
      loadEmails();
    }
  }, [selectedFolder, loadEmails, selectedAccount]);

  // Classification automatique par Christelle
  const handleClassifyEmails = async () => {
    if (!selectedAccount) return;
    setClassifying(true);
    try {
      const resp = await authFetch(`${API}/api/email/classify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_id: selectedAccount.id,
          user_email: authUser?.email,
          account_email: selectedAccount.email_address
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setClassificationStats(data.stats || {});
        let msg = data.message || `${data.classified} emails classifiés`;
        
        // Si aucun email à classer, lancer quand même la génération de rappels
        if (data.classified === 0) {
          const remResp = await authFetch(`${API}/api/email/generate-reminders`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              account_id: selectedAccount.id,
              user_email: authUser?.email,
              account_email: selectedAccount.email_address
            })
          });
          if (remResp.ok) {
            const remData = await remResp.json();
            if (remData.reminders_created > 0) {
              msg += `\n💡 ${remData.reminders_created} suggestions de rappels créées par Christelle`;
            }
          }
        } else if (data.reminders_created > 0) {
          msg += `\n💡 ${data.reminders_created} suggestions de rappels créées par Christelle`;
        }
        
        alert(`✅ Classification terminée !\n${msg}`);
        loadEmails();
      }
    } catch (e) {
      console.error("Erreur classification:", e);
      alert("Erreur lors de la classification");
    }
    setClassifying(false);
  };

  // Rattacher un email à un client
  const handleAttachToClient = async (emailId, clientId) => {
    try {
      const resp = await authFetch(`${API}/api/email/attach-client`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_id: emailId, client_id: clientId })
      });
      if (resp.ok) {
        loadEmails();
        setShowClientSearch(false);
      }
    } catch (e) {
      console.error("Erreur rattachement:", e);
    }
  };

  // Détacher un email d'un client
  const handleDetachFromClient = async (emailId) => {
    try {
      const resp = await authFetch(`${API}/api/email/detach-client`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_id: emailId })
      });
      if (resp.ok) {
        loadEmails();
        if (selectedEmail?.id === emailId) {
          setSelectedEmail({ ...selectedEmail, client: null, client_id: null });
        }
      }
    } catch (e) {
      console.error("Erreur détachement:", e);
    }
  };

  // Autocomplétion des emails
  const handleEmailInputChange = async (value) => {
    setEmailDraft({ ...emailDraft, to: value });
    setSelectedSuggestionIndex(-1);
    
    if (value.length >= 2) {
      setLoadingSuggestions(true);
      try {
        const resp = await authFetch(`${API}/api/email/autocomplete?q=${encodeURIComponent(value)}`);
        if (resp.ok) {
          const data = await resp.json();
          setEmailSuggestions(data.suggestions || []);
          setShowSuggestions(data.suggestions?.length > 0);
        }
      } catch (e) {
        console.error("Erreur autocomplete:", e);
      }
      setLoadingSuggestions(false);
    } else {
      setEmailSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Sélection d'une suggestion
  const handleSelectSuggestion = (suggestion) => {
    setEmailDraft({ 
      ...emailDraft, 
      to: suggestion.email,
      client_id: suggestion.client_id || ""
    });
    setShowSuggestions(false);
    setEmailSuggestions([]);
  };

  // Navigation clavier dans les suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || emailSuggestions.length === 0) return;
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < emailSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : prev);
    } else if (e.key === "Enter" && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(emailSuggestions[selectedSuggestionIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  // Marquer comme lu
  const handleMarkAsRead = async (emailId) => {
    try {
      await authFetch(`${API}/api/email/mark-read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_id: emailId })
      });
    } catch (e) {
      console.error("Erreur mark read:", e);
    }
  };

  // Envoyer email
  const handleSendEmail = async () => {
    if (!emailDraft.to || !emailDraft.subject) return;
    setSending(true);
    try {
      const resp = await authFetch(`${API}/api/email/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from_account_id: selectedAccount?.id,
          to: emailDraft.to,
          subject: emailDraft.subject,
          body: emailDraft.body,
          client_id: emailDraft.client_id,
          user_email: authUser?.email
        })
      });
      if (resp.ok) {
        setEmailDraft({ to: "", subject: "", body: "", client_id: "" });
        setComposing(false);
        setActiveView("inbox");
      }
    } catch (e) {
      console.error("Erreur envoi:", e);
    }
    setSending(false);
  };

  // Sauvegarder brouillon
  const handleSaveDraft = async () => {
    try {
      await authFetch(`${API}/api/email/drafts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...emailDraft,
          account_id: selectedAccount?.id,
          user_email: authUser?.email
        })
      });
      loadDrafts();
    } catch (e) {
      console.error("Erreur sauvegarde brouillon:", e);
    }
  };

  // Demander à Christelle
  const askChristelle = async (action, context = {}) => {
    setChristelleLoading(true);
    try {
      const resp = await authFetch(`${API}/api/email/christelle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action,
          context: christelleQuery || context.context,
          email_content: context.email_content || emailDraft.body,
          user_email: authUser?.email,
          ...context
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setChristelleResponse(data.result);
        
        if (action === "draft" && data.result?.draft) {
          setEmailDraft({ ...emailDraft, body: data.result.draft });
        } else if (action === "reformulate" && data.result?.reformulated) {
          setEmailDraft({ ...emailDraft, body: data.result.reformulated });
        }
      }
    } catch (e) {
      console.error("Erreur Christelle:", e);
    }
    setChristelleLoading(false);
  };

  // Répondre à un email
  const handleReply = (email) => {
    setEmailDraft({
      to: email.from?.replace(/.*<(.+)>.*/, "$1") || email.from,
      subject: email.subject?.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
      body: `\n\n---\nLe ${new Date(email.date).toLocaleDateString('fr-FR')}, ${email.from} a écrit:\n\n${email.body}`,
      client_id: email.client_id || ""
    });
    setComposing(true);
    setActiveView("compose");
    setSelectedEmail(null);
  };

  // Transférer un email
  const handleForward = (email) => {
    setEmailDraft({
      to: "",
      subject: email.subject?.startsWith("Fwd:") ? email.subject : `Fwd: ${email.subject}`,
      body: `\n\n---\nMessage transféré:\nDe: ${email.from}\nDate: ${new Date(email.date).toLocaleDateString('fr-FR')}\nObjet: ${email.subject}\n\n${email.body}`,
      client_id: ""
    });
    setComposing(true);
    setActiveView("compose");
    setSelectedEmail(null);
  };

  // Créer un rappel depuis un email
  // Rappel email
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderEmail, setReminderEmail] = useState(null);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("09:00");
  const [reminderNote, setReminderNote] = useState("");

  const handleCreateReminder = (email) => {
    setReminderEmail(email);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setReminderDate(tomorrow.toISOString().split("T")[0]);
    setReminderTime("09:00");
    setReminderNote(`Relancer : ${email?.subject || ""}`);
    setShowReminderModal(true);
  };

  const submitReminder = async () => {
    if (!reminderEmail || !reminderDate) return;
    try {
      const resp = await authFetch(`${API}/api/email/reminder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_id: reminderEmail._id || reminderEmail.id,
          user_email: authUser?.email,
          reminder_date: reminderDate,
          reminder_time: reminderTime,
          note: reminderNote,
          suggested_by_ai: false
        })
      });
      if (resp.ok) {
        setShowReminderModal(false);
        setReminderEmail(null);
      }
    } catch (e) {
      console.error("Erreur création rappel:", e);
    }
  };

  // Charger la suggestion de réponse automatique avec Christelle
  const loadSuggestedReply = async (email) => {
    if (!email) return;
    
    setLoadingSuggestion(true);
    setSuggestedReply(null);
    
    try {
      const resp = await authFetch(`${API}/api/email/christelle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "suggest_reply",
          email_content: `De: ${email.from}\nObjet: ${email.subject}\nDate: ${email.date}\n\n${email.body}`,
          client_id: email.client_id,
          user_email: authUser?.email
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setSuggestedReply(data.result);
      }
    } catch (e) {
      console.error("Erreur suggestion réponse:", e);
    }
    
    setLoadingSuggestion(false);
  };

  // Utiliser la suggestion comme brouillon de réponse
  const useSuggestedReply = () => {
    if (!suggestedReply?.suggested_reply || !selectedEmail) return;
    
    setEmailDraft({
      to: selectedEmail.from?.replace(/.*<(.+)>.*/, "$1") || selectedEmail.from,
      subject: selectedEmail.subject?.startsWith("Re:") ? selectedEmail.subject : `Re: ${selectedEmail.subject}`,
      body: suggestedReply.suggested_reply,
      client_id: selectedEmail.client_id || ""
    });
    setComposing(true);
    setActiveView("compose");
    setSelectedEmail(null);
    setSuggestedReply(null);
  };

  // Filtrer les emails par catégorie
  const filteredEmails = emails.filter(email => {
    // Filtre par catégorie
    if (activeCategory !== "all" && email.category !== activeCategory) return false;
    
    // Filtre par recherche
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        email.subject?.toLowerCase().includes(q) ||
        email.from?.toLowerCase().includes(q) ||
        email.body?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) { // Moins de 24h
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) { // Moins d'une semaine
      return date.toLocaleDateString('fr-FR', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Rechercher des clients
  const searchClients = async (query) => {
    if (!query || query.length < 2) return;
    try {
      const resp = await authFetch(`${API}/api/crm/clients/search?q=${encodeURIComponent(query)}&limit=10`);
      if (resp.ok) {
        const data = await resp.json();
        setClients(data.clients || []);
      }
    } catch (e) {
      console.error("Erreur recherche clients:", e);
    }
  };

  // Vue détaillée d'un email
  if (selectedEmail) {
    // Charger automatiquement la suggestion de réponse si pas déjà chargée
    if (!suggestedReply && !loadingSuggestion) {
      loadSuggestedReply(selectedEmail);
    }
    
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => { setSelectedEmail(null); setSuggestedReply(null); }}
            className="p-2 hover:bg-slate-800 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">{selectedEmail.subject || "(Sans objet)"}</h2>
            <p className="text-sm text-slate-400">De: {selectedEmail.from}</p>
          </div>
          <div className="flex items-center gap-2">
            <CategoryBadge category={selectedEmail.category} />
            {selectedEmail.client && (
              <ClientBadge 
                client={selectedEmail.client} 
                onDetach={() => handleDetachFromClient(selectedEmail.id)}
              />
            )}
          </div>
        </div>

        {/* Suggestion de réponse Christelle */}
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30 overflow-hidden">
          <div className="p-4 border-b border-purple-500/20 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-300">Christelle Moisi</h3>
                <p className="text-xs text-slate-400">Responsable Administrative IA</p>
              </div>
            </div>
            {suggestedReply?.analysis && (
              <div className="flex items-center gap-3 text-xs">
                {suggestedReply.analysis.has_client_context && (
                  <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded">
                    ✓ Contexte client chargé
                  </span>
                )}
                {suggestedReply.client_context_summary?.has_emails && (
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                    📧 Historique emails
                  </span>
                )}
                {suggestedReply.client_context_summary?.has_sms && (
                  <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded">
                    💬 SMS
                  </span>
                )}
                {suggestedReply.client_context_summary?.has_calls && (
                  <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded">
                    📞 Appels
                  </span>
                )}
              </div>
            )}
          </div>
          
          <div className="p-4">
            {loadingSuggestion ? (
              <div className="flex items-center gap-3 text-purple-300">
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Christelle analyse l'email et prépare une réponse personnalisée...</span>
              </div>
            ) : suggestedReply?.suggested_reply ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-300 italic">
                  "Voici ma proposition de réponse basée sur l'historique du client :"
                </p>
                <div className="bg-slate-900/50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-200">
                    {suggestedReply.suggested_reply}
                  </pre>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={useSuggestedReply}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" /> Utiliser cette réponse
                  </button>
                  <button
                    onClick={() => loadSuggestedReply(selectedEmail)}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" /> Régénérer
                  </button>
                  <button
                    onClick={() => handleReply(selectedEmail)}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2"
                  >
                    <Reply className="w-4 h-4" /> Répondre manuellement
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-slate-400">
                <AlertCircle className="w-5 h-5" />
                <span>Impossible de générer une suggestion pour le moment.</span>
                <button
                  onClick={() => loadSuggestedReply(selectedEmail)}
                  className="text-purple-400 hover:underline"
                >
                  Réessayer
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Actions rapides */}
        <div className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg">
          <button
            onClick={() => handleReply(selectedEmail)}
            className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 rounded-lg flex items-center gap-2 text-sm"
          >
            <Reply className="w-4 h-4" /> Répondre
          </button>
          <button
            onClick={() => handleForward(selectedEmail)}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 text-sm"
          >
            <Forward className="w-4 h-4" /> Transférer
          </button>
          <button
            onClick={() => handleCreateReminder(selectedEmail)}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 text-sm"
          >
            <Bell className="w-4 h-4" /> Rappel
          </button>
          {!selectedEmail.client && (
            <button
              onClick={() => setShowClientSearch(true)}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 rounded-lg flex items-center gap-2 text-sm"
            >
              <Link2 className="w-4 h-4" /> Rattacher client
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={() => askChristelle("analyze_email", { email_content: selectedEmail.body })}
            className="px-3 py-1.5 bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 rounded-lg flex items-center gap-2 text-sm"
          >
            <Sparkles className="w-4 h-4" /> Analyser avec Christelle
          </button>
        </div>

        {/* Modal recherche client */}
        {showClientSearch && (
          <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <User className="w-5 h-5 text-purple-400" />
              <h3 className="font-semibold">Rattacher à un client</h3>
              <button onClick={() => setShowClientSearch(false)} className="ml-auto p-1 hover:bg-slate-800 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <input
              type="text"
              value={clientSearchQuery}
              onChange={(e) => { setClientSearchQuery(e.target.value); searchClients(e.target.value); }}
              placeholder="Rechercher par nom, prénom ou n° dossier..."
              className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg mb-2"
            />
            <div className="max-h-48 overflow-y-auto space-y-1">
              {clients.map(client => (
                <div
                  key={client.id || client.numero_dossier}
                  className="p-2 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 cursor-pointer flex items-center justify-between"
                  onClick={() => handleAttachToClient(selectedEmail.id, client.id || client.numero_dossier)}
                >
                  <div>
                    <p className="font-medium text-sm">{client.nom} {client.prenom}</p>
                    <p className="text-xs text-slate-500">Dossier #{client.numero_dossier} • {client.email}</p>
                  </div>
                  <Link2 className="w-4 h-4 text-purple-400" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analyse Christelle */}
        {christelleResponse?.analysis && (
          <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <h3 className="font-semibold">Analyse de Christelle</h3>
            </div>
            <div className="grid grid-cols-4 gap-4 mb-3">
              <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                <p className="text-xs text-slate-500">Urgence</p>
                <p className={`font-bold ${
                  christelleResponse.analysis.urgency === 'haute' ? 'text-red-400' :
                  christelleResponse.analysis.urgency === 'moyenne' ? 'text-amber-400' : 'text-emerald-400'
                }`}>{christelleResponse.analysis.urgency}</p>
              </div>
              <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                <p className="text-xs text-slate-500">Importance</p>
                <p className={`font-bold ${
                  christelleResponse.analysis.importance === 'haute' ? 'text-amber-400' :
                  christelleResponse.analysis.importance === 'moyenne' ? 'text-blue-400' : 'text-slate-400'
                }`}>{christelleResponse.analysis.importance}</p>
              </div>
              <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                <p className="text-xs text-slate-500">Action</p>
                <p className="font-bold text-cyan-400">{christelleResponse.analysis.action_required ? "Oui" : "Non"}</p>
              </div>
              <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                <p className="text-xs text-slate-500">Catégorie</p>
                <p className="font-bold text-purple-400">{christelleResponse.analysis.category || "Autre"}</p>
              </div>
            </div>
            <p className="text-sm text-slate-300">{christelleResponse.analysis.summary}</p>
            {christelleResponse.analysis.action_description && (
              <p className="text-sm text-amber-400 mt-2">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                {christelleResponse.analysis.action_description}
              </p>
            )}
          </div>
        )}

        {/* Contenu de l'email */}
        <div className="bg-slate-900/50 rounded-xl border border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-700">
            <div>
              <p className="text-sm text-slate-400">De: <span className="text-white">{selectedEmail.from}</span></p>
              <p className="text-sm text-slate-400">À: <span className="text-white">{selectedEmail.to}</span></p>
              <p className="text-sm text-slate-400">Date: <span className="text-white">{formatDate(selectedEmail.date)}</span></p>
            </div>
            {selectedEmail.attachments?.length > 0 && (
              <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-400">{selectedEmail.attachments.length} pièce(s) jointe(s)</span>
              </div>
            )}
          </div>
          
          {/* Corps de l'email */}
          <div className="prose prose-invert max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-200 bg-transparent p-0">
              {selectedEmail.body}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header avec sélection de compte */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="w-6 h-6 text-cyan-400" />
          <div>
            <h2 className="text-lg font-semibold">Messagerie</h2>
            {selectedAccount ? (
              <p className="text-sm text-slate-400">{selectedAccount.email_address}</p>
            ) : (
              <p className="text-sm text-amber-400">Aucun compte email configuré</p>
            )}
          </div>
        </div>
        
        {accounts.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={selectedAccount?.id || ""}
              onChange={(e) => setSelectedAccount(accounts.find(a => a.id === e.target.value))}
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm"
              data-testid="email-account-select"
            >
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.display_name || acc.email_address}
                </option>
              ))}
            </select>
            <button
              onClick={loadEmails}
              className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              title="Actualiser"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        )}
      </div>

      {/* Barre d'actions et recherche */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher dans les emails..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg"
          />
        </div>
        <button
          onClick={handleClassifyEmails}
          disabled={classifying}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg flex items-center gap-2"
          title="Christelle analyse et classe tous les emails non lus"
        >
          {classifying ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          Classer avec Christelle
        </button>
      </div>

      {/* Navigation par catégorie */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => { setActiveView("inbox"); setActiveCategory("all"); }}
          className={`px-3 py-1.5 rounded-lg flex items-center gap-2 whitespace-nowrap ${
            activeView === "inbox" && activeCategory === "all" ? "bg-cyan-500/20 text-cyan-400" : "bg-slate-800 text-slate-300"
          }`}
        >
          <Inbox className="w-4 h-4" />
          Tous ({emailTotal})
        </button>
        {Object.entries(EMAIL_CATEGORIES).map(([key, cat]) => {
          const count = classificationStats[key] || 0;
          if (count === 0 && key !== "urgent" && key !== "important") return null;
          const Icon = cat.icon;
          return (
            <button
              key={key}
              onClick={() => { setActiveView("inbox"); setActiveCategory(key); }}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-2 whitespace-nowrap ${
                activeView === "inbox" && activeCategory === key ? `${cat.bg} ${cat.color}` : "bg-slate-800 text-slate-300"
              }`}
            >
              <Icon className="w-4 h-4" />
              {cat.label} ({count})
            </button>
          );
        })}
        <div className="flex-1" />
        {/* Onglet Listes de diffusion */}
        <button
          onClick={() => { setActiveView("mailing-lists"); setComposing(false); }}
          className={`px-3 py-1.5 rounded-lg flex items-center gap-2 ${
            activeView === "mailing-lists" ? "bg-rose-500/20 text-rose-400" : "bg-slate-800 text-slate-300"
          }`}
        >
          <ListX className="w-4 h-4" />
          Abonnements ({mailingLists.length})
        </button>
        <button
          onClick={() => { setActiveView("drafts"); setComposing(false); }}
          className={`px-3 py-1.5 rounded-lg flex items-center gap-2 ${
            activeView === "drafts" ? "bg-amber-500/20 text-amber-400" : "bg-slate-800 text-slate-300"
          }`}
        >
          <FileText className="w-4 h-4" />
          Brouillons ({drafts.length})
        </button>
        <button
          onClick={() => { setComposing(true); setActiveView("compose"); }}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nouveau message
        </button>
      </div>

      {/* Barre de recherche intelligente IA */}
      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-900/30 to-cyan-900/30 rounded-xl border border-purple-500/30">
        <Sparkles className="w-5 h-5 text-purple-400" />
        <input
          type="text"
          value={aiSearchQuery}
          onChange={(e) => setAiSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleAiSearch()}
          placeholder="Recherche IA : 'emails parlant de factures impayées', 'tous les devis envoyés', 'conversations avec M. Dupont'..."
          className="flex-1 bg-transparent border-none outline-none text-white placeholder-slate-400"
        />
        <button
          onClick={handleAiSearch}
          disabled={aiSearching || !aiSearchQuery.trim()}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg flex items-center gap-2"
        >
          {aiSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Rechercher
        </button>
      </div>

      {/* Layout principal avec dossiers */}
      <div className="flex gap-4">
        {/* Sidebar des dossiers IMAP */}
        <div className="w-48 flex-shrink-0 space-y-1 max-h-[500px] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase">Dossiers</h3>
            {loadingFolders && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
          </div>
          
          {folders.map(folder => {
            const IconComponent = FOLDER_ICONS[folder.icon] || Folder;
            const isSelected = selectedFolder === folder.id && activeView === "inbox";
            return (
              <button
                key={folder.id}
                onClick={() => selectFolder(folder.id)}
                className={`w-full px-3 py-2 rounded-lg flex items-center gap-2 text-sm text-left transition-all ${
                  isSelected ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" : "hover:bg-slate-800 text-slate-300"
                }`}
              >
                <IconComponent className="w-4 h-4 flex-shrink-0" />
                <span className="truncate flex-1">{folder.display_name || folder.name}</span>
                {syncingFolder === folder.id && <Loader2 className="w-3 h-3 animate-spin" />}
              </button>
            );
          })}
          
          {/* Bouton sync tous les dossiers */}
          <div className="pt-2 mt-2 border-t border-slate-700">
            <button
              onClick={() => {
                // Sync les dossiers principaux
                ["INBOX", "INBOX.Sent Messages", "INBOX.Sent"].forEach(f => {
                  if (folders.some(fo => fo.id === f || fo.name === f)) {
                    syncFolder(f);
                  }
                });
              }}
              disabled={syncingFolder !== null}
              className="w-full px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 rounded-lg text-xs flex items-center justify-center gap-2"
            >
              <RefreshCw className={`w-3 h-3 ${syncingFolder ? "animate-spin" : ""}`} />
              Synchroniser tout
            </button>
          </div>
        </div>

        {/* Zone de contenu principale */}
        <div className="flex-1 min-w-0">
      {composing ? (
        /* Composer un email */
        <div className="bg-slate-900/50 rounded-xl border border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Nouveau message</h3>
            <button
              onClick={() => setShowChristellePanel(!showChristellePanel)}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm ${
                showChristellePanel ? "bg-purple-500/20 text-purple-400" : "bg-slate-700 text-slate-300"
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Christelle IA
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {/* Formulaire email */}
            <div className="col-span-2 space-y-3">
              <div className="relative">
                <label className="block text-sm text-slate-400 mb-1">À *</label>
                <div className="relative">
                  <input
                    type="email"
                    value={emailDraft.to}
                    onChange={(e) => handleEmailInputChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => emailSuggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                    placeholder="destinataire@email.com"
                    autoComplete="off"
                    data-testid="email-to-input"
                  />
                  {loadingSuggestions && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                    </div>
                  )}
                </div>
                
                {/* Dropdown suggestions autocomplétion */}
                {showSuggestions && emailSuggestions.length > 0 && (
                  <div 
                    className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                    data-testid="email-suggestions-dropdown"
                  >
                    {emailSuggestions.map((suggestion, idx) => (
                      <button
                        key={suggestion.email}
                        type="button"
                        onClick={() => handleSelectSuggestion(suggestion)}
                        onMouseDown={(e) => e.preventDefault()}
                        className={`w-full px-3 py-2 text-left flex items-center gap-3 hover:bg-slate-700 transition-colors ${
                          idx === selectedSuggestionIndex ? "bg-slate-700" : ""
                        }`}
                        data-testid={`suggestion-${idx}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-purple-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-200 truncate">
                            {suggestion.name || suggestion.email}
                          </div>
                          <div className="text-xs text-slate-400 truncate">
                            {suggestion.email}
                          </div>
                          {suggestion.company && (
                            <div className="text-xs text-slate-500 truncate">
                              {suggestion.company}
                            </div>
                          )}
                        </div>
                        {suggestion.client_id && (
                          <div className="flex-shrink-0 px-2 py-0.5 bg-purple-500/20 rounded text-xs text-purple-400">
                            Client CRM
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Badge client lié */}
                {emailDraft.client_id && (
                  <div className="mt-2 flex items-center gap-2 px-2 py-1 bg-purple-500/20 rounded-lg w-fit">
                    <Link2 className="w-3 h-3 text-purple-400" />
                    <span className="text-xs text-purple-300">Email lié au dossier client</span>
                    <button 
                      type="button"
                      onClick={() => setEmailDraft({...emailDraft, client_id: ""})}
                      className="p-0.5 hover:bg-purple-500/30 rounded"
                      title="Délier du client"
                    >
                      <X className="w-3 h-3 text-purple-400" />
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Objet *</label>
                <input
                  type="text"
                  value={emailDraft.subject}
                  onChange={(e) => setEmailDraft({...emailDraft, subject: e.target.value})}
                  className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                  placeholder="Objet de l'email"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Message</label>
                <textarea
                  value={emailDraft.body}
                  onChange={(e) => setEmailDraft({...emailDraft, body: e.target.value})}
                  className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg resize-none"
                  rows={10}
                  placeholder="Rédigez votre message..."
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSendEmail}
                  disabled={sending}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-lg flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {sending ? "Envoi..." : "Envoyer"}
                </button>
                <button
                  onClick={handleSaveDraft}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Sauvegarder brouillon
                </button>
                <button
                  onClick={() => { setComposing(false); setActiveView("inbox"); }}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                >
                  Annuler
                </button>
              </div>
            </div>

            {/* Panneau Christelle */}
            {showChristellePanel && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  <h4 className="font-semibold text-purple-300">Christelle Moisi</h4>
                </div>
                <p className="text-xs text-slate-400 mb-4">
                  Assistante administrative IA
                </p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Contexte / Instructions</label>
                    <textarea
                      value={christelleQuery}
                      onChange={(e) => setChristelleQuery(e.target.value)}
                      className="w-full p-2 bg-slate-800/50 border border-purple-500/30 rounded-lg text-sm resize-none"
                      rows={3}
                      placeholder="Ex: Répondre poliment à une demande de devis..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => askChristelle("draft")}
                      disabled={christelleLoading}
                      className="px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg text-sm flex items-center justify-center gap-1"
                    >
                      {christelleLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Rédiger
                    </button>
                    <button
                      onClick={() => askChristelle("reformulate")}
                      disabled={christelleLoading || !emailDraft.body}
                      className="px-3 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg text-sm"
                    >
                      Reformuler
                    </button>
                  </div>
                </div>

                {christelleResponse && (
                  <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-xs text-slate-400 mb-1">Suggestion de Christelle :</p>
                    <p className="text-sm text-slate-200">{christelleResponse.draft || christelleResponse.reformulated || "Réponse reçue"}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : activeView === "inbox" ? (
        /* Liste des emails */
        <div className="space-y-2">
          {loading ? (
            <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-700">
              <RefreshCw className="w-8 h-8 text-cyan-400 mx-auto mb-3 animate-spin" />
              <p className="text-slate-400">Chargement des emails...</p>
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-700">
              <Inbox className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">
                {searchQuery ? "Aucun email trouvé" : activeCategory !== "all" ? `Aucun email ${EMAIL_CATEGORIES[activeCategory]?.label.toLowerCase()}` : "Aucun email"}
              </p>
            </div>
          ) : (
            filteredEmails.map(email => (
              <div 
                key={email.id}
                className={`p-4 bg-slate-900/50 rounded-lg border cursor-pointer transition-all hover:border-cyan-500/50 ${
                  email.is_read ? "border-slate-700" : "border-cyan-500/30 bg-cyan-500/5"
                }`}
                onClick={() => { setSelectedEmail(email); handleMarkAsRead(email.id); }}
              >
                <div className="flex items-start gap-4">
                  {/* Indicateur non lu */}
                  <div className={`w-2 h-2 rounded-full mt-2 ${email.is_read ? "bg-transparent" : "bg-cyan-400"}`} />
                  
                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium truncate">{email.from}</p>
                      <CategoryBadge category={email.category} />
                      {email.client && (
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs flex items-center gap-1">
                          <Link2 className="w-3 h-3" />
                          {email.client.nom}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-300 truncate">{email.subject}</p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-1">{email.body}</p>
                  </div>
                  
                  {/* Date et actions */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-slate-500">{formatDate(email.date)}</p>
                    {email.has_attachments && (
                      <Paperclip className="w-3 h-3 text-slate-500 ml-auto mt-1" />
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          
          {/* Bandeau pagination */}
          {!loading && emails.length > 0 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <span className="text-sm text-slate-400">
                Affichage {emails.length} sur {emailTotal} emails
              </span>
              {emailHasMore ? (
                <button
                  onClick={() => loadEmails(true)}
                  disabled={loadingMore}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-600 text-white rounded-lg text-sm flex items-center gap-2 transition-colors"
                  data-testid="load-more-emails"
                >
                  {loadingMore ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Chargement...
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Charger les 30 suivants
                    </>
                  )}
                </button>
              ) : (
                <span className="text-sm text-slate-500">Tous les emails sont affichés</span>
              )}
            </div>
          )}
        </div>
      ) : activeView === "mailing-lists" ? (
        /* Listes de diffusion */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ListX className="w-6 h-6 text-rose-400" />
              <div>
                <h3 className="font-semibold">Listes de diffusion & Newsletters</h3>
                <p className="text-sm text-slate-400">Gérez vos abonnements emails</p>
              </div>
            </div>
            <button
              onClick={loadMailingLists}
              disabled={loadingMailingLists}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loadingMailingLists ? "animate-spin" : ""}`} />
              Actualiser
            </button>
          </div>

          {loadingMailingLists ? (
            <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-700">
              <Loader2 className="w-8 h-8 text-rose-400 mx-auto mb-3 animate-spin" />
              <p className="text-slate-400">Analyse des listes de diffusion...</p>
            </div>
          ) : mailingLists.length === 0 ? (
            <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-700">
              <MailX className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">Aucune liste de diffusion détectée</p>
              <p className="text-xs text-slate-500 mt-2">Synchronisez vos emails pour détecter les newsletters</p>
            </div>
          ) : (
            <div className="space-y-2">
              {mailingLists.map(list => (
                <div 
                  key={list.id}
                  className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-rose-500/30 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Mail className="w-4 h-4 text-rose-400" />
                        <p className="font-medium truncate">{list.name}</p>
                        <span className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-300">
                          {list.email_count} emails
                        </span>
                      </div>
                      <p className="text-sm text-slate-400 truncate">{list.email}</p>
                      {list.sample_subjects?.length > 0 && (
                        <p className="text-xs text-slate-500 mt-2 truncate">
                          Exemple: {list.sample_subjects[0]}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span>Premier email: {formatDate(list.first_email)}</span>
                        <span>•</span>
                        <span>Dernier: {formatDate(list.last_email)}</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      {(list.unsubscribe_link || list.unsubscribe_email) ? (
                        <button
                          onClick={() => handleUnsubscribe(list)}
                          disabled={unsubscribing === list.id}
                          className="px-4 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 rounded-lg text-sm flex items-center gap-2"
                        >
                          {unsubscribing === list.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Unlink className="w-4 h-4" />
                          )}
                          Se désabonner
                        </button>
                      ) : (
                        <span className="text-xs text-slate-500 italic">
                          Pas de lien de désabonnement
                        </span>
                      )}
                      {list.unsubscribe_link && (
                        <a
                          href={list.unsubscribe_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-cyan-400 hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Ouvrir le lien
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeView === "ai-search" ? (
        /* Résultats de recherche IA */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-purple-400" />
              <div>
                <h3 className="font-semibold">Résultats de recherche IA</h3>
                <p className="text-sm text-slate-400">"{aiSearchQuery}"</p>
              </div>
            </div>
            <button
              onClick={() => { setActiveView("inbox"); setAiSearchResults(null); }}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
          </div>

          {aiSearchResults ? (
            <>
              {/* Info sur la portée de la recherche */}
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <FolderOpen className="w-3 h-3" />
                  {aiSearchResults.search_scope || "Tous les dossiers"}
                </span>
                <span>•</span>
                <span>{aiSearchResults.total_searched || 0} emails analysés</span>
                {aiSearchResults.folders_synced && (
                  <>
                    <span>•</span>
                    <span className="text-emerald-400">Dossiers synchronisés</span>
                  </>
                )}
              </div>

              {/* Résumé IA */}
              {aiSearchResults.summary && (
                <div className="p-4 bg-purple-900/20 rounded-xl border border-purple-500/30">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-purple-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-purple-300 mb-2">Analyse de Christelle</p>
                      <p className="text-slate-300 whitespace-pre-wrap">{aiSearchResults.summary}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Emails trouvés */}
              <div className="space-y-2">
                <p className="text-sm text-slate-400">{aiSearchResults.emails?.length || 0} emails correspondants</p>
                {(aiSearchResults.emails || []).map((email, idx) => (
                  <div 
                    key={idx}
                    onClick={() => setSelectedEmail(email)}
                    className="p-4 bg-slate-900/50 rounded-lg border border-purple-500/20 hover:border-purple-500/40 cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium truncate">{email.subject || "(Sans objet)"}</p>
                          {email.relevance_score && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                              {Math.round(email.relevance_score * 100)}% pertinent
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 truncate">{email.from}</p>
                        {email.match_reason && (
                          <p className="text-xs text-purple-400 mt-1">{email.match_reason}</p>
                        )}
                        {email.has_attachments && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-cyan-400">
                            <Paperclip className="w-3 h-3" />
                            Pièces jointes
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">{formatDate(email.date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-700">
              <Loader2 className="w-8 h-8 text-purple-400 mx-auto mb-3 animate-spin" />
              <p className="text-slate-400">Analyse en cours...</p>
            </div>
          )}
        </div>
      ) : activeView === "drafts" ? (
        /* Brouillons */
        <div className="space-y-2">
          {drafts.length === 0 ? (
            <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-700">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">Aucun brouillon</p>
            </div>
          ) : (
            drafts.map(draft => (
              <div 
                key={draft.id}
                className="p-4 bg-slate-900/50 rounded-lg border border-amber-500/30 hover:border-amber-500/50 cursor-pointer"
                onClick={() => {
                  setEmailDraft(draft);
                  setComposing(true);
                  setActiveView("compose");
                }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-amber-400">Brouillon</p>
                    <p className="text-sm text-slate-300">{draft.subject || "(Sans objet)"}</p>
                    <p className="text-xs text-slate-500">À: {draft.to || "(Non défini)"}</p>
                  </div>
                  <p className="text-xs text-slate-500">
                    {formatDate(draft.updated_at)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}
        </div>
      </div>
      
      {/* Modal Rappel Email */}
      {showReminderModal && reminderEmail && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowReminderModal(false)}>
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-400" />
              Créer un rappel
            </h3>
            
            <div className="mb-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
              <p className="text-xs text-slate-400">Email lié</p>
              <p className="text-sm text-white truncate">{reminderEmail.subject}</p>
              <p className="text-xs text-slate-500">{reminderEmail.from}</p>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Date du rappel</label>
                <input
                  type="date"
                  value={reminderDate}
                  onChange={e => setReminderDate(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="reminder-date"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Heure</label>
                <input
                  type="time"
                  value={reminderTime}
                  onChange={e => setReminderTime(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="reminder-time"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Note</label>
                <textarea
                  value={reminderNote}
                  onChange={e => setReminderNote(e.target.value)}
                  rows={2}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm resize-none"
                  data-testid="reminder-note"
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowReminderModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
              >
                Annuler
              </button>
              <button
                onClick={submitReminder}
                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-500 rounded-lg text-sm font-medium"
                data-testid="submit-reminder"
              >
                Créer le rappel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}